/**
 * A cheap first-line local map of "what has this device recently requested" — a hint, not a
 * guard. The server enforces `track_cooldown` and rate limits regardless; this only saves a
 * round trip for the obvious case and lets the UI grey a button before the request fires.
 *
 * On a kiosk this `localStorage` is shared by every punter who walks up, so it reads as
 * venue-wide. On a phone it's genuinely per-person. Same code, different deployment — see
 * touchscreen-frontend.md §Guardrails.
 */

const STORAGE_KEY = "touchscreen:recently_requested";

interface RecentEntry {
  requestedAtMs: number;
}

type RecentMap = Record<string, RecentEntry>;

function readMap(): RecentMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as RecentMap;
  } catch {
    return {};
  }
}

function writeMap(map: RecentMap): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Storage full or blocked — the server remains authoritative, so this is a pure UX miss.
  }
}

export function markRequested(smId: string): void {
  const map = readMap();
  map[smId] = { requestedAtMs: Date.now() };
  writeMap(map);
}

/** `true` if this device requested `smId` within `cooldownMinutes`. A hint — always let the server decide. */
export function isCoolingDown(smId: string, cooldownMinutes: number): boolean {
  const entry = readMap()[smId];
  if (!entry) return false;
  const elapsedMinutes = (Date.now() - entry.requestedAtMs) / 60_000;
  return elapsedMinutes < cooldownMinutes;
}
